import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import Header from './Header';
import Footer from './Footer';
import { PayeeHeader } from './PayeeHeader';
import { ArrowLeft, CreditCard, QrCode, Loader2, CheckCircle, Shield, Lock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PagamentoPage = () => {
  const navigate = useNavigate();
  const [cadastroData, setCadastroData] = useState(null);
  const [planoSelecionado, setPlanoSelecionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);

  useEffect(() => {
    const cadastro = sessionStorage.getItem('cadastroData');
    const plano = sessionStorage.getItem('planoSelecionado');
    
    if (!cadastro || !plano) {
      navigate('/cadastro');
      return;
    }
    
    setCadastroData(JSON.parse(cadastro));
    
    // Normalizar dados do plano para garantir que priceValue existe
    const planoData = JSON.parse(plano);
    const normalizedPlano = {
      ...planoData,
      priceValue: planoData.priceValue || planoData.price || 0,
      features: planoData.features || []
    };
    setPlanoSelecionado(normalizedPlano);
  }, [navigate]);

  const createPayment = async () => {
    if (!selectedMethod) {
      setError('Selecione uma forma de pagamento');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/payments/create-pix`, {
        customer_name: cadastroData.adminNome,
        customer_email: cadastroData.email,
        customer_phone: cadastroData.adminTelefone,
        customer_cpf: cadastroData.adminCpf?.replace(/\D/g, ''),
        amount: planoSelecionado.priceValue,
        description: `Livrum - Plano ${planoSelecionado.title}`,
        plan_id: planoSelecionado.id,
        biblioteca_nome: cadastroData.bibliotecaNome,
        admin_data_nascimento: cadastroData.adminDataNascimento,
        admin_endereco: cadastroData.adminEndereco,
        biblioteca_instituicao: cadastroData.bibliotecaInstituicao,
        biblioteca_endereco: cadastroData.bibliotecaEndereco,
        biblioteca_email: cadastroData.bibliotecaEmail,
        biblioteca_telefone: cadastroData.bibliotecaTelefone,
        biblioteca_num_usuarios: cadastroData.bibliotecaNumUsuarios,
        biblioteca_num_exemplares: cadastroData.bibliotecaNumExemplares,
        senha: cadastroData.senha,
        payment_method: selectedMethod,
        // Campos para Biblioteca Digital
        library_type: cadastroData.libraryType || planoSelecionado.type || 'fisica',
        valor_assinatura: cadastroData.valorAssinatura ? parseFloat(cadastroData.valorAssinatura) : null,
        terms_accepted: Boolean(cadastroData.termsAccepted)
      });
      
      if (response.data.checkout_url) {
        sessionStorage.setItem('pending_payment_id', response.data.payment_id);
        window.location.href = response.data.checkout_url;
      } else {
        setError('Erro ao gerar link de pagamento');
        setLoading(false);
      }
    } catch (err) {
      console.error('Erro ao criar pagamento:', err);
      setError(err.response?.data?.detail || 'Erro ao criar pagamento. Tente novamente.');
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (!cadastroData || !planoSelecionado) {
    return (
      <div className="pagamento-page">
        <Header />
        <main className="pagamento-main">
          <div className="pagamento-loading">
            <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#6A68A3' }} />
            <p>Carregando...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="pagamento-page">
      <Header />
      <main className="pagamento-main">
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>
          {/* Header com voltar */}
          <button 
            onClick={() => navigate('/escolher-plano')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'none',
              border: 'none',
              color: '#6A68A3',
              cursor: 'pointer',
              marginBottom: '1.5rem',
              fontSize: '0.95rem'
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>

          <h1 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 700, 
            color: '#2D3748', 
            marginBottom: '0.5rem',
            textAlign: 'center'
          }}>
            Finalizar Pagamento
          </h1>
          <p style={{ 
            color: '#718096', 
            textAlign: 'center', 
            marginBottom: '2rem' 
          }}>
            Escolha como deseja pagar
          </p>

          <div className="pagamento-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 350px', gap: '2rem', alignItems: 'start' }}>
            {/* Opções de Pagamento */}
            <div>
              <PayeeHeader />
              <Card style={{ marginBottom: '1rem', cursor: 'pointer', border: selectedMethod === 'pix' ? '2px solid #6A68A3' : '1px solid #e2e8f0' }}
                onClick={() => setSelectedMethod('pix')}
                data-testid="payment-method-pix"
              >
                <CardContent style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      background: selectedMethod === 'pix' ? '#6A68A3' : '#f0f0f0',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <QrCode className="w-8 h-8" style={{ color: selectedMethod === 'pix' ? 'white' : '#6A68A3' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#2D3748', marginBottom: '0.25rem' }}>
                        PIX
                      </h3>
                      <p style={{ color: '#718096', fontSize: '0.9rem' }}>
                        Pagamento instantâneo via QR Code ou código
                      </p>
                    </div>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      border: selectedMethod === 'pix' ? '2px solid #6A68A3' : '2px solid #cbd5e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {selectedMethod === 'pix' && (
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#6A68A3' }} />
                      )}
                    </div>
                  </div>
                  {selectedMethod === 'pix' && (
                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '0.75rem', 
                      background: '#f0fdf4', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <CheckCircle className="w-4 h-4" style={{ color: '#22c55e' }} />
                      <span style={{ color: '#166534', fontSize: '0.85rem' }}>
                        Aprovação imediata após o pagamento
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card style={{ cursor: 'pointer', border: selectedMethod === 'card' ? '2px solid #6A68A3' : '1px solid #e2e8f0' }}
                onClick={() => setSelectedMethod('card')}
                data-testid="payment-method-card"
              >
                <CardContent style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      background: selectedMethod === 'card' ? '#6A68A3' : '#f0f0f0',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CreditCard className="w-8 h-8" style={{ color: selectedMethod === 'card' ? 'white' : '#6A68A3' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#2D3748' }}>
                          Cartão de Crédito
                        </h3>
                        <span style={{ 
                          background: '#6A68A3', 
                          color: 'white', 
                          fontSize: '0.7rem', 
                          padding: '0.15rem 0.5rem', 
                          borderRadius: '50px',
                          fontWeight: 600
                        }}>
                          até 4x
                        </span>
                      </div>
                      <p style={{ color: '#718096', fontSize: '0.9rem' }}>
                        Parcele em até 4x no cartão
                      </p>
                    </div>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      border: selectedMethod === 'card' ? '2px solid #6A68A3' : '2px solid #cbd5e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {selectedMethod === 'card' && (
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#6A68A3' }} />
                      )}
                    </div>
                  </div>
                  {selectedMethod === 'card' && (
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ 
                        padding: '0.75rem', 
                        background: '#f5f3ff', 
                        borderRadius: '8px',
                        marginBottom: '0.5rem'
                      }}>
                        <p style={{ color: '#5b21b6', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                          Opções de parcelamento:
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                          <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>1x de {formatCurrency(planoSelecionado?.priceValue)}</div>
                          <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>2x de {formatCurrency(planoSelecionado?.priceValue / 2)}</div>
                          <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>3x de {formatCurrency(planoSelecionado?.priceValue / 3)}</div>
                          <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>4x de {formatCurrency(planoSelecionado?.priceValue / 4)}</div>
                        </div>
                      </div>
                      <div style={{ 
                        padding: '0.75rem', 
                        background: '#eff6ff', 
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <Shield className="w-4 h-4" style={{ color: '#3b82f6' }} />
                        <span style={{ color: '#1e40af', fontSize: '0.85rem' }}>
                          Pagamento seguro processado pelo Asaas
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Erro */}
              {error && (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '1rem', 
                  background: '#fef2f2', 
                  borderRadius: '8px',
                  color: '#dc2626',
                  fontSize: '0.9rem'
                }}>
                  {error}
                </div>
              )}

              {/* Botão de Pagar */}
              <Button 
                className="btn-primary-custom"
                style={{ 
                  width: '100%', 
                  marginTop: '1.5rem', 
                  padding: '1rem',
                  fontSize: '1.1rem'
                }}
                onClick={createPayment}
                disabled={loading || !selectedMethod}
                data-testid="pay-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Pagar {formatCurrency(planoSelecionado.priceValue)}
                  </>
                )}
              </Button>

              {/* Segurança */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '0.5rem',
                marginTop: '1rem',
                color: '#718096',
                fontSize: '0.85rem'
              }}>
                <Lock className="w-4 h-4" />
                Pagamento seguro via Asaas
              </div>
            </div>

            {/* Resumo do Pedido */}
            <Card style={{ position: 'sticky', top: '2rem' }}>
              <CardHeader style={{ borderBottom: '1px solid #e2e8f0', padding: '1.25rem' }}>
                <CardTitle style={{ fontSize: '1rem', color: '#2D3748' }}>
                  Resumo do Pedido
                </CardTitle>
              </CardHeader>
              <CardContent style={{ padding: '1.25rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Plano</p>
                  <p style={{ fontWeight: 600, color: '#2D3748' }}>{planoSelecionado.title}</p>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Biblioteca</p>
                  <p style={{ fontWeight: 500, color: '#2D3748' }}>{cadastroData.bibliotecaNome}</p>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Responsável</p>
                  <p style={{ fontWeight: 500, color: '#2D3748' }}>{cadastroData.adminNome}</p>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '0.25rem' }}>E-mail</p>
                  <p style={{ fontWeight: 500, color: '#2D3748', fontSize: '0.9rem' }}>{cadastroData.email}</p>
                </div>

                <div style={{ 
                  borderTop: '1px solid #e2e8f0', 
                  paddingTop: '1rem',
                  marginTop: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#718096' }}>Total</span>
                    <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#6A68A3' }}>
                      {formatCurrency(planoSelecionado.priceValue)}
                    </span>
                  </div>
                </div>

                {/* Features do plano */}
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '1rem', 
                  background: '#f8fafc', 
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}>
                  <p style={{ fontWeight: 600, color: '#2D3748', marginBottom: '0.5rem' }}>Incluído no plano:</p>
                  <ul style={{ color: '#718096', paddingLeft: '1rem', margin: 0 }}>
                    {planoSelecionado.features?.slice(0, 4).map((feature, idx) => (
                      <li key={idx} style={{ marginBottom: '0.25rem' }}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PagamentoPage;
